import React, { useCallback, useMemo, useRef } from "react";
import { FieldAnchor, FieldCoordinate } from "../pages/logger/types";
import BasketballCourtSVG from "./BasketballCourtSVG";

interface Player {
  id: string;
  full_name: string;
  jersey_number: number;
  position: string;
}

type PositionGroup = "guard" | "forward" | "center" | "other";

const POSITION_GROUP_ORDER: PositionGroup[] = [
  "guard",
  "forward",
  "center",
  "other",
];

const normalizePosition = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z]/g, "")
    .trim();

const getPositionGroup = (raw?: string | null): PositionGroup => {
  const normalized = normalizePosition(raw || "");
  if (!normalized) return "other";
  if (
    normalized.includes("pg") ||
    normalized.includes("sg") ||
    normalized.includes("guard") ||
    normalized.includes("pointguard") ||
    normalized.includes("shootingguard")
  ) {
    return "guard";
  }
  if (
    normalized.includes("sf") ||
    normalized.includes("pf") ||
    normalized.includes("forward") ||
    normalized.includes("smallforward") ||
    normalized.includes("powerforward")
  ) {
    return "forward";
  }
  if (normalized.includes("c") || normalized.includes("center")) {
    return "center";
  }
  return "other";
};

const getGroupMeta = (group: PositionGroup) => {
  switch (group) {
    case "guard":
      return {
        dot: "bg-cyan-500",
        border: "border-cyan-500/70",
        badge: "bg-cyan-500/10 text-cyan-800 border-cyan-500/40",
      };
    case "forward":
      return {
        dot: "bg-amber-500",
        border: "border-amber-500/70",
        badge: "bg-amber-500/10 text-amber-800 border-amber-500/40",
      };
    case "center":
      return {
        dot: "bg-rose-500",
        border: "border-rose-500/70",
        badge: "bg-rose-500/10 text-rose-800 border-rose-500/40",
      };
    default:
      return {
        dot: "bg-slate-500",
        border: "border-slate-400/70",
        badge: "bg-slate-500/10 text-slate-800 border-slate-400/40",
      };
  }
};

const sortPlayersByGroup = (players: Player[]) =>
  [...players].sort((a, b) => {
    const groupA = getPositionGroup(a.position);
    const groupB = getPositionGroup(b.position);
    const groupDelta =
      POSITION_GROUP_ORDER.indexOf(groupA) -
      POSITION_GROUP_ORDER.indexOf(groupB);
    if (groupDelta !== 0) return groupDelta;
    return a.jersey_number - b.jersey_number;
  });

interface BasketballCourtProps {
  homeTeamName: string;
  awayTeamName: string;
  homePlayers: Player[];
  awayPlayers: Player[];
  expelledPlayerIds?: Set<string>;
  disciplinaryStatusByPlayer?: Record<
    string,
    { foulCount: number; ejected: boolean; disqualified: boolean }
  >;
  onPlayerClick: (
    player: Player,
    anchor: FieldAnchor,
    location: [number, number],
    side: "home" | "away",
  ) => void;
  onDestinationClick?: (coordinate: FieldCoordinate) => void;
  showDestinationControls?: boolean;
  overlay?: React.ReactNode;
  flipSides?: boolean;
}

const BasketballCourt: React.FC<BasketballCourtProps> = ({
  homeTeamName,
  awayTeamName,
  homePlayers,
  awayPlayers,
  expelledPlayerIds,
  disciplinaryStatusByPlayer,
  onPlayerClick,
  onDestinationClick,
  overlay,
  showDestinationControls = false,
  flipSides = false,
}) => {
  const fieldRef = useRef<HTMLDivElement | null>(null);

  const fieldBounds = useMemo(
    () => ({ left: 4, right: 96, top: 4, bottom: 96 }),
    [],
  );

  const maybeFlipX = useCallback(
    (xPercent: number) => (flipSides ? 100 - xPercent : xPercent),
    [flipSides],
  );

  // Basketball court position mapping (5 players per side)
  const getPositionCoordinates = (index: number, side: "home" | "away") => {
    const displaySide = flipSides ? (side === "home" ? "away" : "home") : side;
    // Basketball: PG at top, SG at wing, SF/PF at forward, C at center/post
    const positions = [
      // PG - top of key
      displaySide === "home" ? { x: 25, y: 50 } : { x: 75, y: 50 },
      // SG - wing
      displaySide === "home" ? { x: 30, y: 20 } : { x: 70, y: 20 },
      // SF - wing
      displaySide === "home" ? { x: 30, y: 80 } : { x: 70, y: 80 },
      // PF - post
      displaySide === "home" ? { x: 15, y: 35 } : { x: 85, y: 35 },
      // C - low post
      displaySide === "home" ? { x: 15, y: 65 } : { x: 85, y: 65 },
    ];
    return positions[Math.min(index, positions.length - 1)];
  };

  /** Convert % coordinates to court coordinates (0-94, 0-50) */
  const resolveCourtLocation = (xPercent: number, yPercent: number) =>
    [Number((xPercent / 100) * 94), Number((yPercent / 100) * 50)] as [
      number,
      number,
    ];

  const buildCoordinate = (
    xPercent: number,
    yPercent: number,
  ): FieldCoordinate => {
    const flippedX = maybeFlipX(xPercent);
    let outOfBoundsEdge: FieldCoordinate["outOfBoundsEdge"] = null;
    const isOutOfBounds =
      flippedX < fieldBounds.left ||
      flippedX > fieldBounds.right ||
      yPercent < fieldBounds.top ||
      yPercent > fieldBounds.bottom;
    if (flippedX < fieldBounds.left) {
      outOfBoundsEdge = "left";
    } else if (flippedX > fieldBounds.right) {
      outOfBoundsEdge = "right";
    } else if (yPercent < fieldBounds.top) {
      outOfBoundsEdge = "top";
    } else if (yPercent > fieldBounds.bottom) {
      outOfBoundsEdge = "bottom";
    }
    const clampedX = Math.min(100, Math.max(0, flippedX));
    const clampedY = Math.min(100, Math.max(0, yPercent));
    return {
      xPercent: clampedX,
      yPercent: clampedY,
      statsbomb: resolveCourtLocation(clampedX, clampedY),
      isOutOfBounds,
      outOfBoundsEdge,
    };
  };

  const handleFieldClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!onDestinationClick || !fieldRef.current) return;
      const rect = fieldRef.current.getBoundingClientRect();
      const xPercent = ((event.clientX - rect.left) / rect.width) * 100;
      const yPercent = ((event.clientY - rect.top) / rect.height) * 100;
      onDestinationClick(buildCoordinate(xPercent, yPercent));
    },
    [onDestinationClick],
  );

  const renderPlayerListItem = (
    player: Player,
    index: number,
    side: "home" | "away",
  ) => {
    const { x, y } = getPositionCoordinates(index, side);
    const anchor = { xPercent: x, yPercent: y } satisfies FieldAnchor;
    const location = resolveCourtLocation(x, y);
    const isHome = side === "home";
    const group = getPositionGroup(player.position);
    const meta = getGroupMeta(group);
    const foulStatus = disciplinaryStatusByPlayer?.[player.id];
    const hasFouls = (foulStatus?.foulCount || 0) > 0;
    const isExpelled = expelledPlayerIds?.has(player.id) ?? false;

    return (
      <button
        key={player.id}
        type="button"
        disabled={isExpelled}
        onClick={(event) => {
          if (isExpelled) return;
          event.stopPropagation();
          onPlayerClick(player, anchor, location, side);
        }}
        data-testid={`field-player-${player.id}`}
        data-player-row="true"
        data-position-group={group}
        className={`pointer-events-auto w-full max-w-[clamp(340px,32vw,520px)] rounded-md border-l-4 px-[clamp(0.72rem,0.58rem+0.42vw,1.35rem)] py-[clamp(0.44rem,0.32rem+0.28vw,1rem)] text-left text-[clamp(0.84rem,0.7rem+0.34vw,1.2rem)] font-semibold shadow-sm transition-colors ${
          isExpelled
            ? "border-slate-400 bg-slate-200 text-slate-500 opacity-70 cursor-not-allowed saturate-50"
            : isHome
              ? "border-red-300 bg-white/95 text-slate-900"
              : "border-blue-300 bg-white/95 text-slate-900"
        } ${meta.border}`}
        title={`${player.full_name} (${player.position})`}
      >
        <div className="flex items-center gap-[clamp(0.5rem,0.38rem+0.24vw,1rem)]">
          <span
            className={`h-[clamp(0.5rem,0.38rem+0.2vw,0.85rem)] w-[clamp(0.5rem,0.38rem+0.2vw,0.85rem)] rounded-full ${meta.dot}`}
          />
          <span
            data-testid={`field-player-number-${player.id}`}
            className="inline-flex min-w-[clamp(1.7rem,1.35rem+0.5vw,2.7rem)] items-center justify-center rounded-full bg-slate-900 text-white text-[clamp(0.72rem,0.6rem+0.24vw,1.08rem)] px-[clamp(0.42rem,0.32rem+0.18vw,0.78rem)] py-[clamp(0.2rem,0.16rem+0.1vw,0.4rem)]"
          >
            {player.jersey_number}
          </span>
          <span
            data-testid={`field-player-name-${player.id}`}
            className="flex-1 truncate text-[clamp(0.82rem,0.68rem+0.34vw,1.2rem)]"
          >
            {player.full_name}
          </span>
          <span
            data-testid={`field-player-position-${player.id}`}
            className={`text-[clamp(0.66rem,0.54rem+0.2vw,0.96rem)] uppercase tracking-wider border px-[clamp(0.4rem,0.3rem+0.16vw,0.72rem)] py-[clamp(0.18rem,0.14rem+0.1vw,0.38rem)] rounded-full ${meta.badge}`}
          >
            {player.position}
          </span>
          {hasFouls && (
            <span
              data-testid={`field-player-foul-count-${player.id}`}
              className="inline-flex items-center rounded-md border border-orange-400/50 bg-orange-500/20 px-[clamp(0.25rem,0.2rem+0.12vw,0.45rem)] py-[clamp(0.2rem,0.16rem+0.1vw,0.38rem)] text-[clamp(0.56rem,0.5rem+0.16vw,0.78rem)] font-bold text-orange-300"
            >
              {foulStatus!.foulCount}F
            </span>
          )}
          {isExpelled && (
            <span
              data-testid={`field-player-disabled-badge-${player.id}`}
              className="inline-flex items-center rounded-md border border-slate-500/80 bg-slate-700/80 px-[clamp(0.25rem,0.2rem+0.12vw,0.45rem)] py-[clamp(0.2rem,0.16rem+0.1vw,0.38rem)] text-[clamp(0.56rem,0.5rem+0.16vw,0.78rem)] font-semibold uppercase tracking-wide text-slate-100"
            >
              Ejected
            </span>
          )}
        </div>
      </button>
    );
  };

  return (
    <div className="relative w-full px-6 py-6">
      <div className="relative overflow-visible">
        <div
          ref={fieldRef}
          data-testid="basketball-court"
          onClick={handleFieldClick}
          className="w-full aspect-[1.88] rounded-xl relative overflow-hidden border-4 border-white shadow-inner"
        >
          {/* Accurate NBA court (shared SVG) */}
          <BasketballCourtSVG className="absolute inset-0 w-full h-full" />

          {/* Team Labels */}
          <div className="absolute top-2 left-2 text-white font-bold text-sm sm:text-base bg-black/45 px-2.5 py-1.5 rounded-md">
            {flipSides ? `${awayTeamName} (Away)` : `${homeTeamName} (Home)`}
          </div>
          <div className="absolute top-2 right-2 text-white font-bold text-sm sm:text-base bg-black/45 px-2.5 py-1.5 rounded-md">
            {flipSides ? `${homeTeamName} (Home)` : `${awayTeamName} (Away)`}
          </div>

          {/* Players (list layout) */}
          <div className="absolute inset-0 flex items-center justify-between px-6 py-10 pointer-events-none">
            <div
              className="w-[40%] 2xl:w-[30%] 2xl:max-w-[360px] space-y-2"
              data-testid="field-side-left"
            >
              {sortPlayersByGroup(flipSides ? awayPlayers : homePlayers).map(
                (p, i) =>
                  renderPlayerListItem(p, i, flipSides ? "away" : "home"),
              )}
            </div>
            <div
              className="w-[40%] 2xl:w-[30%] 2xl:max-w-[360px] space-y-2 flex flex-col items-end"
              data-testid="field-side-right"
            >
              {sortPlayersByGroup(flipSides ? homePlayers : awayPlayers).map(
                (p, i) =>
                  renderPlayerListItem(p, i, flipSides ? "home" : "away"),
              )}
            </div>
          </div>
          {overlay ? (
            <div className="absolute inset-0 z-20 pointer-events-none">
              <div className="relative h-full w-full pointer-events-none">
                {overlay}
              </div>
            </div>
          ) : null}
        </div>

        {/* ─── Border-zone out-of-bounds destination strips ─── */}
        {onDestinationClick &&
          showDestinationControls &&
          (() => {
            const COLS = 6;
            const ROWS = 4;
            const COL_PCT = 100 / COLS;
            const ROW_PCT = 100 / ROWS;
            const H_THICK = 8;
            const V_THICK = 6;

            const zones: Array<{
              id: string;
              edge: "top" | "bottom" | "left" | "right";
              idx: number;
              coordX: number;
              coordY: number;
            }> = [
              ...Array.from({ length: COLS }, (_, i) => ({
                id: `border-zone-top-${i}`,
                edge: "top" as const,
                idx: i,
                coordX: maybeFlipX((i + 0.5) * COL_PCT),
                coordY: 0,
              })),
              ...Array.from({ length: COLS }, (_, i) => ({
                id: `border-zone-bottom-${i}`,
                edge: "bottom" as const,
                idx: i,
                coordX: maybeFlipX((i + 0.5) * COL_PCT),
                coordY: 100,
              })),
              ...Array.from({ length: ROWS }, (_, i) => ({
                id: `border-zone-left-${i}`,
                edge: "left" as const,
                idx: i,
                coordX: maybeFlipX(0),
                coordY: (i + 0.5) * ROW_PCT,
              })),
              ...Array.from({ length: ROWS }, (_, i) => ({
                id: `border-zone-right-${i}`,
                edge: "right" as const,
                idx: i,
                coordX: maybeFlipX(100),
                coordY: (i + 0.5) * ROW_PCT,
              })),
            ];

            return (
              <div className="absolute inset-0 pointer-events-none overflow-visible z-20">
                {zones.map((zone) => {
                  let style: React.CSSProperties;
                  const isBaseline =
                    zone.edge === "left" || zone.edge === "right";

                  switch (zone.edge) {
                    case "top": {
                      const leftPct = flipSides
                        ? 100 - (zone.idx + 1) * COL_PCT
                        : zone.idx * COL_PCT;
                      style = {
                        left: `${leftPct}%`,
                        top: 0,
                        width: `${COL_PCT}%`,
                        height: `${H_THICK}%`,
                      };
                      break;
                    }
                    case "bottom": {
                      const leftPct = flipSides
                        ? 100 - (zone.idx + 1) * COL_PCT
                        : zone.idx * COL_PCT;
                      style = {
                        left: `${leftPct}%`,
                        bottom: 0,
                        width: `${COL_PCT}%`,
                        height: `${H_THICK}%`,
                      };
                      break;
                    }
                    case "left": {
                      style = flipSides
                        ? {
                            right: 0,
                            top: `${zone.idx * ROW_PCT}%`,
                            width: `${V_THICK}%`,
                            height: `${ROW_PCT}%`,
                          }
                        : {
                            left: 0,
                            top: `${zone.idx * ROW_PCT}%`,
                            width: `${V_THICK}%`,
                            height: `${ROW_PCT}%`,
                          };
                      break;
                    }
                    case "right": {
                      style = flipSides
                        ? {
                            left: 0,
                            top: `${zone.idx * ROW_PCT}%`,
                            width: `${V_THICK}%`,
                            height: `${ROW_PCT}%`,
                          }
                        : {
                            right: 0,
                            top: `${zone.idx * ROW_PCT}%`,
                            width: `${V_THICK}%`,
                            height: `${ROW_PCT}%`,
                          };
                      break;
                    }
                  }

                  return (
                    <button
                      key={zone.id}
                      type="button"
                      data-testid={zone.id}
                      className={`pointer-events-auto absolute z-30 flex items-center justify-center
                      text-[9px] font-bold uppercase tracking-wider transition-colors cursor-pointer
                      ${
                        isBaseline
                          ? "bg-red-900/50 hover:bg-red-700/60 text-red-200 border border-red-500/40 hover:border-red-300/60"
                          : "bg-amber-900/50 hover:bg-amber-700/60 text-amber-200 border border-amber-500/40 hover:border-amber-300/60"
                      }`}
                      style={style}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDestinationClick(
                          buildCoordinate(zone.coordX, zone.coordY),
                        );
                      }}
                      title={`Out — ${zone.edge}`}
                    >
                      <span
                        className={`inline-flex items-center gap-1 ${
                          isBaseline ? "-rotate-90" : ""
                        }`}
                      >
                        OUT
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })()}
      </div>
    </div>
  );
};

export default BasketballCourt;
